import { useCallback, useContext, useEffect, useState } from 'react';
import { useMobileOrientation, isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import {
  countPlacedCharacters,
  getLocalDateWOTime,
  moveActiveCharacterDown,
  setNextActiveCharacter,
  savePuzzleToLS,
  saveFinishToLS,
} from 'src/utils';
import { DifficultyContext } from 'src/context/difficultyContext';

import { useInitializeGame } from './useInitializeGame';
import { useDefineCharactersList } from './useDefineCharactersList';
import { useTimers } from './useTimers';
import { useControls } from './useControls';

export function useGame() {
  const navigate = useNavigate();
  const difficultyContext = useContext(DifficultyContext);
  const orientation = useMobileOrientation();
  const [shouldResumeInPortrait, setShouldResumeInPortrait] = useState(true);
  const [shouldResumeAfterBecomeVisible, setShouldResumeAfterBecomeVisible] = useState(true);

  const {
    isLoading,
    phraseData,
    phraseError,
    phraseNoSpaces,
    scrambledNoSpaces,
    isInSwapMode,
    setIsInSwapMode,
    isRevealed,
    setIsRevealed,
    isCorrect,
    setIsCorrect,
    shouldTransitGridToLine,
    strikesLeft,
    setStrikesLeft,
    sideSize,
    mainElementRef,
    recalculateCellSize,
    params,
    initialPuzzleData,
  } = useInitializeGame();

  const {
    timer,
    isPaused,
    setIsPaused,
    pausedTimer,
    warmupTimer,
    setWarmupTimer,
    visualTimer,
    setVisualTimer,
  } = useTimers({
    phraseData,
    isCorrect,
    isRevealed,
    strikesLeft,
    initialPuzzleData,
  });

  const { charactersList, setCharactersList } = useDefineCharactersList({
    phraseData,
    phraseNoSpaces,
    scrambledNoSpaces,
    initialPuzzleData,
    warmupTimer,
  });

  const {
    initialSwapCharacter,
    handleSolve,
    handleKeyControlsMouseClicks,
    handleRevealClick,
    handleSwapSelection,
    handleDropAndSwap,
    togglePlayPause,
    handlePeekActiveCharacter,
    isActiveCharacterFlashingTimeout,
  } = useControls({
    charactersList,
    setCharactersList,
    isPaused,
    setIsPaused,
    setIsRevealed,
    setIsInSwapMode,
    phraseNoSpaces,
    setStrikesLeft,
    setVisualTimer,
    warmupTimer,
  });

  const togglePlayPauseOnVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'hidden') {
      if (isPaused) {
        setShouldResumeAfterBecomeVisible(false);
      }
      setIsPaused(true);
    } else {
      if (shouldResumeAfterBecomeVisible) {
        setIsPaused(false);
      }
    }
  }, [isPaused, setIsPaused, shouldResumeAfterBecomeVisible, setShouldResumeAfterBecomeVisible]);

  /**
   * Contains main game logic to drop characters based on time when in Grid-Mode
   */
  useEffect(() => {
    if (!isPaused && !isInSwapMode) {
      const activeCharacterIndex = charactersList?.findIndex((character) => character.isActive);

      if (
        charactersList &&
        typeof activeCharacterIndex !== 'undefined' &&
        activeCharacterIndex !== -1
      ) {
        // If active character IS the last one - force `isSolved` state and exit
        if (
          phraseData &&
          countPlacedCharacters(charactersList) + 1 === phraseData.character_count
        ) {
          handleSolve();
          return;
        }

        // If active character is NOT at the bottom - move one block down and exit
        if (
          charactersList[activeCharacterIndex].currentPosition.y <
          charactersList[activeCharacterIndex].correctPosition.y
        ) {
          const characterListWithNewPosition = moveActiveCharacterDown(charactersList);
          setCharactersList(characterListWithNewPosition);

          if (
            characterListWithNewPosition[activeCharacterIndex].currentPosition.y <
            characterListWithNewPosition[activeCharacterIndex].correctPosition.y
          ) {
            return;
          }

          setCharactersList(setNextActiveCharacter(characterListWithNewPosition, false));
          return;
        }

        setCharactersList(setNextActiveCharacter(charactersList, false));
      }
    }
  }, [timer, isInSwapMode, isPaused]);

  /**
   * Observes charactersList to change to swap mode
   */
  useEffect(() => {
    if (!charactersList) {
      return;
    }
    if (!charactersList.some((character) => !character.isPlaced)) {
      setIsInSwapMode(true);
    }
  }, [charactersList]);

  /**
   * Observes state of the game and save to LS on change if exist
   */
  useEffect(() => {
    if (isLoading) {
      return;
    }
    // save state only for today's puzzle
    const isTodayPuzzle = !Boolean(params.date);

    if (
      !isTodayPuzzle ||
      !phraseData ||
      !charactersList ||
      !(timer || visualTimer || warmupTimer)
    ) {
      return;
    }

    savePuzzleToLS({
      charactersList,
      phraseData,
      timer,
      visualTimer,
      warmupTimer,
      level: difficultyContext?.level,
    });
  }, [
    isLoading, charactersList, warmupTimer, phraseData, timer, visualTimer, params,
    difficultyContext?.level,
  ]);

  /**
   * Resume today's puzzle logic
   */
  const [isShownResumeGameModal, setIsShownResumeGameModal] = useState(false);

  const finishTheGame = useCallback(() => {
    setStrikesLeft(0);
    handleSolve();
    setIsShownResumeGameModal(false);
    const gameDate = params.date || getLocalDateWOTime();
    saveFinishToLS(gameDate, phraseData!, charactersList!);
    navigate('/');
  }, [setStrikesLeft, handleSolve, params.date, phraseData, charactersList, navigate]);

  const resumeTheGame = useCallback(() => {
    setIsPaused(false);
    setIsShownResumeGameModal(false);
  }, [setIsPaused]);

  useEffect(() => {
    if (
      initialPuzzleData &&
      !isCorrect &&
      !isRevealed &&
      strikesLeft &&
      !difficultyContext?.hasChangedLevel
    ) {
      setIsShownResumeGameModal(true);
      setIsPaused(true);
    }
    // Only re-run when initialPuzzleData changes. hasChangedLevel is checked but not a dependency
    // to avoid re-triggering when it resets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPuzzleData]);

  /**
   * Observes `charactersList` state and decides whether game is solved correctly or not
   */
  useEffect(() => {
    if (charactersList && !isRevealed) {
      const isCorrect = charactersList.every((character) => {
        return (
          character.correctPosition.x.includes(character.currentPosition.x) &&
          character.correctPosition.y === character.currentPosition.y
        );
      });
      if (isCorrect) {
        setIsCorrect(isCorrect);
        setIsInSwapMode(true);
      }
    }
  }, [charactersList, isRevealed, setIsCorrect]);

  /**
   * Subscribes to orientation change and locks screen from interactions
   */
  useEffect(() => {
    // Pausing the game and storing the paused state
    if (isMobile && orientation.isLandscape) {
      if (!isPaused) {
        setShouldResumeInPortrait(true);
      }
      setIsPaused(true);
    }

    // Resuming the game if it wasn't paused before switching to landscape mode
    if (isMobile && orientation.isPortrait && shouldResumeInPortrait) {
      setIsPaused(false);
      setShouldResumeInPortrait(false);
    }
  }, [
    isPaused,
    setIsPaused,
    shouldResumeInPortrait,
    setShouldResumeInPortrait,
    isMobile,
    orientation.isLandscape,
    orientation.isPortrait,
  ]);

  /**
   * Subscribes to `visibilitychange` event and pauses the game till user returns
   */
  useEffect(() => {
    window.document.addEventListener('visibilitychange', togglePlayPauseOnVisibilityChange);
    return () =>
      window.document.removeEventListener('visibilitychange', togglePlayPauseOnVisibilityChange);
  }, [togglePlayPauseOnVisibilityChange]);

  /**
   * Subscribes to multiple flags to see if the game is over - no matter success or failure
   */
  useEffect(() => {
    if (isCorrect || isRevealed || !strikesLeft) {
      const gameDate = params.date || getLocalDateWOTime();
      saveFinishToLS(gameDate, phraseData!, charactersList!);
    }
  }, [isCorrect, strikesLeft, isRevealed, params.date, phraseData, charactersList]);

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview' });
  }, []);

  return {
    isLoading,
    response: phraseData,
    phraseError,
    charactersList,
    timer,
    pausedTimer,
    isPaused,
    togglePlayPause,
    isInSwapMode,
    handleSolve,
    handleSwapSelection,
    handleDropAndSwap,
    handleKeyControlsMouseClicks,
    initialSwapCharacter,
    isCorrect,
    isRevealed,
    handleRevealClick,
    handlePeekActiveCharacter,
    shouldTransitGridToLine,
    strikesLeft,
    sideSize,
    mainElementRef,
    warmupTimer,
    setWarmupTimer,
    recalculateCellSize,
    visualTimer,
    params,
    isActiveCharacterFlashingTimeout,
    unfinishedToday: {
      isShown: isShownResumeGameModal,
      onExit: finishTheGame,
      onResume: resumeTheGame,
    },
  };
}
